import React, { useState, useEffect, useImperativeHandle, forwardRef, useRef } from 'react';
import './Formulario.css';
import { apiCall } from '../utils/auth';

// Helper: format number to BRL
const formatBRL = (v) => {
  const n = Number(v || 0);
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

function FormularioMateriaPrima({ 
  tarefaId = null, 
  onMateriaPrimaAdded = null, 
  modoEdicao = false,
  tipoId = 'tarefa', // 'tarefa' ou 'montagem'
  onClose = null,
  onValorTotalChange = null,
  // Orçamento: matérias-primas ficam dentro de um serviço do orçamento (ServicoOrcamentoId)
  servicoOrcamentoId = null,
  filtrarPorServico = false, // true: lista só as do servicoOrcamentoId (null = as sem serviço)
  ocultarAdicao = false,     // true: só lista as já alocadas (sem linhas para alocar novas)
  onExistentesChange = null, // recebe a quantidade de matérias-primas já alocadas listadas
  somenteLeitura = false,    // true: não permite alterar nem remover as já alocadas
  tarefaCancelada = false,   // true: a tarefa está como cancelada — ao salvar, pergunta o destino de todo o material alocado
  tarefaConcluindo = false,  // true: a tarefa está sendo concluída — ao salvar, pergunta se sobrou ou se perdeu matéria-prima
  onDestinoConfirmado = null // chamado quando o destino do material (tarefa cancelada ou concluída) é confirmado (o pai continua o salvar)
}, ref) {
  const [novoMaterial, setNovoMaterial] = useState({
    tarefaId: tarefaId || '',
    dataExecucao: '',
    valorTotal: 0
  });

  const [linhas, setLinhas] = useState([{ estoqueMaterialId: '', quantidade: '', quantidadePrevista: '' }]);
  const [materiaisEstoque, setMateriaisEstoque] = useState([]);
  const [loadingEstoque, setLoadingEstoque] = useState(false);
  const [alocando, setAlocando] = useState(false);
  const [materiasPrimas, setMateriasPrimas] = useState([]);
  // Modal de deleção customizado
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [qtdDanificada, setQtdDanificada] = useState('');
  const [qtdRetornada, setQtdRetornada] = useState('');
  const [deleting, setDeleting] = useState(false);
  // Edição da quantidade de uma matéria-prima já alocada: { [id]: valor digitado }
  const [quantidadeEditada, setQuantidadeEditadaEstado] = useState({});
  const quantidadeEditadaRef = useRef({});
  const setQuantidadeEditada = (valor) => {
    const novo = typeof valor === 'function' ? valor(quantidadeEditadaRef.current) : valor;
    quantidadeEditadaRef.current = novo;
    setQuantidadeEditadaEstado(novo);
  };
  // Nova quantidade quando o modal de remoção vem de uma redução (ex.: 5 → 3); null = remoção pelo ×
  const [reducaoAlvo, setReducaoAlvo] = useState(null);
  const [ajustandoId, setAjustandoId] = useState(null);
  // Alterações nas já alocadas só são gravadas no "Salvar" do formulário:
  // diminuição confirmada no pop-up: { [id]: { nova, danificada, retornada } }
  const [reducoesConfirmadas, setReducoesConfirmadasEstado] = useState({});
  const reducoesConfirmadasRef = useRef({});
  const setReducoesConfirmadas = (valor) => {
    const novo = typeof valor === 'function' ? valor(reducoesConfirmadasRef.current) : valor;
    reducoesConfirmadasRef.current = novo;
    setReducoesConfirmadasEstado(novo);
  };
  const [erroPendencias, setErroPendencias] = useState('');
  const containerRef = useRef(null);
  // Tarefa cancelada: destino de todo o material alocado { [id]: { danificada, retornada } }
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [cancelDistribuicao, setCancelDistribuicao] = useState({});
  // true depois de responder o pop-up do cancelamento (mesmo deixando tudo vazio: o material fica na tarefa)
  const cancelamentoRespondidoRef = useRef(false);
  // Tarefa concluída: quanto se perdeu e quanto sobrou (volta ao estoque) de cada matéria-prima { [id]: { danificada, retornada } }.
  // conclusaoRef: null = ainda não respondeu o pop-up; objeto = respondido (o que for informado é gravado no Salvar)
  const [conclusaoModalOpen, setConclusaoModalOpen] = useState(false);
  const [conclusaoDistribuicao, setConclusaoDistribuicao] = useState({});
  const conclusaoRef = useRef(null);

  // Expor método para o pai fazer POST das matérias-primas pendentes e forçar recarga
  useImperativeHandle(ref, () => ({
    // Chamar ANTES de salvar o formulário: false se falta confirmar alguma diminuição (mostra o aviso e rola até aqui)
    validarAlteracoes: () => validarAlteracoes(),
    // Grava as alterações de quantidade pendentes (aumentos e diminuições confirmadas)
    aplicarAlteracoes: () => aplicarAlteracoes(),

    // Método para fazer POST de todas as linhas pendentes
    // Pode receber tarefaIdParam como parâmetro (para quando a tarefa acaba de ser criada)
    alocarMateriaisParaTarefa: async (tarefaIdParam = null, servicoOrcamentoIdParam = null) => {
      // Primeiro as alterações nas já alocadas (aumentos e diminuições confirmadas)
      await aplicarAlteracoes();

      const tarefaIdFinal = tarefaIdParam || tarefaId;
      const servicoOrcamentoIdFinal = servicoOrcamentoIdParam ?? servicoOrcamentoId ?? null;
      if (!tarefaIdFinal) {
        throw new Error('ID não disponível.');
      }

      const linhasValidas = linhas.filter(l => l.estoqueMaterialId && Number(l.quantidade) > 0);
      if (linhasValidas.length === 0) {
        return { success: true, count: 0, message: 'Nenhum material para alocar' };
      }

      try {
        for (const linha of linhasValidas) {
          const material = materiaisEstoque.find(m => (m.id || m.Id) === parseInt(linha.estoqueMaterialId));
          const custo = material?.custoUnitario || material?.CustoUnitario || 0;
          const valor = custo * parseFloat(linha.quantidade);
          const quantidadeSolicitada = parseFloat(linha.quantidade);
          const quantidadeDisponivel = material?.quantidade || material?.Quantidade || 0;

          // Lógica de quantidadePrevista:
          // - Para TAREFA, MONTAGEM, GARANTIA e MANUTENÇÃO: modo criação (modoEdicao=false) -> quantidadePrevista = quantidade
          //                                                  modo edição (modoEdicao=true) -> quantidadePrevista = null
          // - Para outros tipos: usa o valor do input se fornecido
          let quantidadePrevistaFinal;
          if (tipoId === 'tarefa' || tipoId === 'montagem' || tipoId === 'garantia' || tipoId === 'manutencao') {
            quantidadePrevistaFinal = modoEdicao ? null : quantidadeSolicitada;
          } else {
            quantidadePrevistaFinal = linha.quantidadePrevista ? parseFloat(linha.quantidadePrevista) : null;
          }

          const payload = {
            id: 0,
            descricao: material?.titulo || material?.Titulo || material?.descricao || material?.Descricao || '',
            tipo: material?.tipoEstoqueMaterialNome || material?.TipoEstoqueMaterialNome || 'Material',
            valor: valor,
            fornecedorId: null,
            producaoId: null,
            estoqueMaterialId: parseInt(linha.estoqueMaterialId),
            unidadeMedidaId: material?.unidadeMedidaId || material?.UnidadeMedidaId || null,
            quantidade: quantidadeSolicitada,
            quantidadePrevista: quantidadePrevistaFinal,
            dataExecucao: novoMaterial.dataExecucao || new Date().toISOString().split('T')[0],
            tarefaId: tipoId === 'tarefa' ? parseInt(tarefaIdFinal) : null,
            montagemId: tipoId === 'montagem' ? parseInt(tarefaIdFinal) : null,
            garantiaId: tipoId === 'garantia' ? parseInt(tarefaIdFinal) : null,
            manutencaoId: tipoId === 'manutencao' ? parseInt(tarefaIdFinal) : null,
            orcamentoId: tipoId === 'orcamento' ? parseInt(tarefaIdFinal) : null,
            servicoOrcamentoId: tipoId === 'orcamento' && servicoOrcamentoIdFinal ? parseInt(servicoOrcamentoIdFinal) : null
          };

          // 1. POST de MateriaPrima
          await apiCall('MateriaPrima', 'POST', payload);

          // 2. Se não tem quantidade suficiente em estoque, fazer POST de MateriaPrimaFaltante
          // (Tarefa, garantia e manutenção: o servidor já cria o faltante ao alocar)
          if (tipoId === 'orcamento' && quantidadeDisponivel < quantidadeSolicitada) {
            const quantidadeFaltante = quantidadeSolicitada - quantidadeDisponivel;
            const payloadFaltante = {
              id: 0,
              descricao: material?.titulo || material?.Titulo || material?.descricao || material?.Descricao || '',
              tipo: material?.tipoEstoqueMaterialNome || material?.TipoEstoqueMaterialNome || 'Material',
              valor: valor,
              fornecedorId: null,
              producaoId: null,
              estoqueMaterialId: parseInt(linha.estoqueMaterialId),
              unidadeMedidaId: material?.unidadeMedidaId || material?.UnidadeMedidaId || null,
              quantidadeFaltante: quantidadeFaltante,
              dataExecucao: novoMaterial.dataExecucao || new Date().toISOString().split('T')[0],
              tarefaId: tipoId === 'tarefa' ? parseInt(tarefaIdFinal) : null,
              montagemId: tipoId === 'montagem' ? parseInt(tarefaIdFinal) : null,
              garantiaId: tipoId === 'garantia' ? parseInt(tarefaIdFinal) : null,
              manutencaoId: tipoId === 'manutencao' ? parseInt(tarefaIdFinal) : null,
              orcamentoId: tipoId === 'orcamento' ? parseInt(tarefaIdFinal) : null
            };

            try {
              await apiCall('MateriaPrimaFaltante', 'POST', payloadFaltante);
              console.log(`✅ MateriaPrimaFaltante criada: ${quantidadeFaltante} ${material?.unidadeMedida?.sigla || ''}`);
            } catch (errFaltante) {
              console.warn('⚠️ Aviso ao criar MateriaPrimaFaltante:', errFaltante.message);
              // Não bloqueia o fluxo se falhar a criação de faltante
            }
          }
        }

        // Limpar formulário após sucesso
        setLinhas([{ estoqueMaterialId: '', quantidade: '', quantidadePrevista: '' }]);
        await carregarMateriaPrimasDaTarefa();

        return { success: true, count: linhasValidas.length };
      } catch (error) {
        throw error;
      }
    },
    
    // Método para obter linhas pendentes (sem fazer POST)
    obterLinhasPendentes: () => {
      return linhas.filter(l => l.estoqueMaterialId && Number(l.quantidade) > 0);
    },

    // Método para limpar o formulário
    limpar: () => {
      setLinhas([{ estoqueMaterialId: '', quantidade: '', quantidadePrevista: '' }]);
      setNovoMaterial({
        tarefaId: tarefaId || '',
        dataExecucao: '',
        valorTotal: 0
      });
    }
    ,
    // Método para forçar recarregar matérias-primas alocadas (aceita id param opcional)
    carregarMateriasAlocadas: async (tarefaIdParam = null) => {
      try {
        await carregarMateriaPrimasDaTarefa(tarefaIdParam);
        return { success: true };
      } catch (err) {
        console.error('Erro ao carregar matérias-primas via imperative handle:', err);
        return { success: false, error: err };
      }
    }
  }));

  // Carregar materiais de estoque e matérias-primas alocadas
  useEffect(() => {
    carregarMateriaisEstoque();
    if (tarefaId) {
      carregarMateriaPrimasDaTarefa();
    }
  }, [tarefaId, tipoId, servicoOrcamentoId]);

  const carregarMateriaisEstoque = async () => {
    try {
      setLoadingEstoque(true);
      const result = await apiCall('EstoqueMaterial');
      if (Array.isArray(result)) {
        setMateriaisEstoque(result);
      } else if (result.data && Array.isArray(result.data)) {
        setMateriaisEstoque(result.data);
      }
    } catch (error) {
      console.error('Erro ao carregar estoque:', error);
    } finally {
      setLoadingEstoque(false);
    }
  };

  const carregarMateriaPrimasDaTarefa = async (tarefaIdParam = null) => {
    const idToUse = tarefaIdParam || tarefaId;
    if (!idToUse) {
      console.log('⚠️ tarefaId não disponível');
      return;
    }
    
    try {
      console.log(`🔍 Buscando matérias-primas para ${tipoId} ID: ${tarefaId}`);
      
      // Tenta primeiro o endpoint genérico
      let result;
      if (tipoId === 'montagem') {
        result = await apiCall(`MateriaPrima/por-montagem/${idToUse}`);
      } else if (tipoId === 'tarefa') {
        result = await apiCall(`MateriaPrima/por-tarefa/${idToUse}`);
      } else if (tipoId === 'manutencao') {
        result = await apiCall(`MateriaPrima/por-manutencao/${idToUse}`);
      } else if (tipoId === 'garantia') {
        result = await apiCall(`MateriaPrima/por-garantia/${idToUse}`);
      } else if (tipoId === 'orcamento') {
        result = await apiCall(`MateriaPrima/por-orcamento/${idToUse}`);
      } else {
        // Outros tipos: usa por-id como fallback
        result = await apiCall(`MateriaPrima/por-id/${idToUse}/${tipoId}`);
      }
      
      if (result && Array.isArray(result)) {
        // Orçamento: cada serviço mostra só as suas matérias-primas
        const lista = filtrarPorServico
          ? result.filter(mp => (mp.servicoOrcamentoId ?? mp.ServicoOrcamentoId ?? null) === (servicoOrcamentoId ?? null))
          : result;
        setMateriasPrimas(lista);
        console.log(`✅ Matérias-primas da ${tipoId}:`, result);
      } else if (!result || (Array.isArray(result) && result.length === 0)) {
        console.log(`ℹ️ Nenhuma matéria-prima encontrada para esta ${tipoId}`);
        setMateriasPrimas([]);
      }
    } catch (error) {
      console.log(`⚠️ Erro ao buscar matérias-primas:`, error.message);
      setMateriasPrimas([]);
    }
  };

  // Calcular valor total dinamicamente
  const valorTotal = linhas.reduce((total, linha) => {
    if (!linha.estoqueMaterialId) return total;
    const material = materiaisEstoque.find(m => (m.id || m.Id) === parseInt(linha.estoqueMaterialId));
    const custo = material?.custoUnitario || material?.CustoUnitario || 0;
    const qty = parseFloat(linha.quantidade) || 0;
    return total + (custo * qty);
  }, 0);

  useEffect(() => {
    if (onExistentesChange) onExistentesChange(materiasPrimas.length);
  }, [materiasPrimas.length]);

  // handleSubmit removido - o POST é feito pelo pai via useImperativeHandle (alocarMateriaisParaTarefa)

  // ── Matéria-prima já alocada ──
  const idMateriaPrima = (mp) => mp.materiaPrimaId || mp.id || mp.Id;
  const quantidadeAtualMP = (mp) => parseFloat(mp?.quantidade ?? mp?.Quantidade ?? 0) || 0;
  const estoqueIdDe = (mp) =>
    mp?.estoqueMaterialId || mp?.EstoqueMaterialId || mp?.estoqueMaterial?.id || mp?.estoqueMaterial?.Id ||
    mp?.EstoqueMaterial?.id || mp?.EstoqueMaterial?.Id || null;
  // Custo unitário do momento da alocação (valor ÷ quantidade); sem quantidade, usa o do estoque.
  // No orçamento o valor é o do previsto (a reserva pode ser menor quando faltou estoque).
  const custoUnitarioDe = (mp) => {
    const prevista = parseFloat(mp?.quantidadePrevista ?? mp?.QuantidadePrevista) || 0;
    const quantidade = tipoId === 'orcamento' && prevista > 0 ? prevista : quantidadeAtualMP(mp);
    const valor = parseFloat(mp?.valor ?? mp?.Valor) || 0;
    if (quantidade > 0) return valor / quantidade;
    return parseFloat(mp?.estoqueMaterialCustoUnitario ?? mp?.EstoqueMaterialCustoUnitario) || 0;
  };
  const arredondarValor = (v) => Math.round(v * 100) / 100;

  // Orçamento: o previsto acompanha a mudança de quantidade (ex.: reservado 5 → 3 baixa o previsto em 2)
  const previstoOrcamento = (mp, novaQuantidade) =>
    Math.max(0, (parseFloat(mp.quantidadePrevista ?? mp.QuantidadePrevista) || quantidadeAtualMP(mp)) + novaQuantidade - quantidadeAtualMP(mp));

  // Valor mostrado na linha: com uma quantidade digitada (ainda não confirmada), já mostra o valor novo
  const valorAtualMP = (mp) => parseFloat(mp?.valor ?? mp?.Valor) || 0;
  const valorExibidoMP = (mp) => {
    const digitada = parseFloat(quantidadeEditada[idMateriaPrima(mp)]);
    if (isNaN(digitada) || digitada < 0 || digitada === quantidadeAtualMP(mp)) return valorAtualMP(mp);
    const quantidade = tipoId === 'orcamento' ? previstoOrcamento(mp, digitada) : digitada;
    return arredondarValor(custoUnitarioDe(mp) * quantidade);
  };

  // Valor das matérias-primas já vinculadas, já com as quantidades digitadas
  const valorExistentes = materiasPrimas.reduce((total, mp) => total + valorExibidoMP(mp), 0);

  // Notificar o pai com linhas novas + já vinculadas (para o custo do orçamento)
  useEffect(() => {
    if (onValorTotalChange) onValorTotalChange(valorTotal + valorExistentes);
  }, [valorTotal, valorExistentes]);

  const limparQuantidadeEditada = (id) => {
    setQuantidadeEditada(prev => {
      const { [id]: _removido, ...resto } = prev;
      return resto;
    });
  };

  // Quantidade digitada: aumentar fica pendente até o "Salvar" (sem ✓);
  // diminuir abre o pop-up para dizer quanto foi perdido e quanto volta ao estoque
  const novaDigitada = (mp) => {
    const v = parseFloat(quantidadeEditadaRef.current[idMateriaPrima(mp)]);
    return isNaN(v) ? quantidadeAtualMP(mp) : v;
  };
  const reducaoConfirmadaDe = (mp) => {
    const c = reducoesConfirmadasRef.current[idMateriaPrima(mp)];
    return c && c.nova === novaDigitada(mp) ? c : null;
  };
  const precisaConfirmar = (mp) => novaDigitada(mp) < quantidadeAtualMP(mp) && !reducaoConfirmadaDe(mp);

  const alterarQuantidadeDigitada = (mp, valor) => {
    const id = idMateriaPrima(mp);
    setQuantidadeEditada(prev => ({ ...prev, [id]: valor }));
    setErroPendencias('');
  };

  const aplicarQuantidade = (mp) => {
    const nova = novaDigitada(mp);
    if (nova < 0) {
      alert('Informe uma quantidade válida.');
      return;
    }
    if (nova < quantidadeAtualMP(mp)) abrirModalDelete(mp, nova);
  };

  // Grava a diminuição confirmada: o servidor devolve ao estoque a parte que volta e registra a perdida
  // na tabela de matéria-prima desperdiçada (tudo de uma vez)
  const processarReducao = async (mp, nova, c) => {
    await apiCall(`MateriaPrima/${idMateriaPrima(mp)}/reduzir`, 'POST', {
      novaQuantidade: nova,
      quantidadeDesperdicada: c.danificada,
      quantidadeRetornadaEstoque: c.retornada,
      motivo: c.cancelamento ? 'Tarefa cancelada' : 'Diminuição da quantidade'
    });
  };

  // Mostra o aviso em vermelho e rola até a matéria-prima
  const avisarPendencia = (mensagem) => {
    setErroPendencias(mensagem);
    setTimeout(() => containerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 50);
  };

  const faltaDestinoDoCancelamento = () =>
    tarefaCancelada && !cancelamentoRespondidoRef.current && materiasPrimas.some(mp => quantidadeAtualMP(mp) > 0);

  // Campo do pop-up de cancelamento/conclusão: começa vazio; vazio conta como 0
  const qtdCampo = (v) => Number(v) || 0;
  const valorCampo = (valor) => (valor === '' ? '' : Math.max(0, Math.floor(Number(valor)) || 0));

  const abrirPopupCancelamento = () => {
    const inicial = {};
    // Por material (o mesmo material lançado várias vezes vem somado); os campos começam vazios
    gruposCancelamento().forEach(g => {
      inicial[g.chave] = cancelDistribuicao[g.chave] || { danificada: '', retornada: '' };
    });
    setCancelDistribuicao(inicial);
    setCancelModalOpen(true);
  };

  const validarAlteracoes = () => {
    if (faltaDestinoDoCancelamento()) {
      // Tarefa cancelada: antes de salvar, pergunta o destino de todo o material alocado
      setErroPendencias('A tarefa está cancelada: informe o destino de todo o material alocado (quanto foi perdido e quanto volta ao estoque) para salvar.');
      abrirPopupCancelamento();
      return false;
    }
    const pendentes = materiasPrimas.filter(precisaConfirmar);
    if (pendentes.length > 0) {
      avisarPendencia(`Você diminuiu a quantidade de ${pendentes.length} matéria(s)-prima(s) sem confirmar. Por favor, confirme clicando no ✓ ao lado da quantidade para informar quanto foi perdido e quanto volta ao estoque.`);
      return false;
    }
    if (tarefaConcluindo && conclusaoRef.current === null && materiasPrimas.some(mp => novaDigitada(mp) > 0)) {
      // Tarefa sendo concluída: antes de salvar, pergunta se sobrou ou se perdeu alguma matéria-prima
      abrirPopupConclusao();
      return false;
    }
    setErroPendencias('');
    return true;
  };

  const aplicarAlteracoes = async () => {
    const erros = [];
    let alterou = false;
    const destinosConclusao = conclusaoRef.current || {};
    for (const mp of materiasPrimas) {
      const id = idMateriaPrima(mp);
      const atual = quantidadeAtualMP(mp);
      const nova = novaDigitada(mp);
      const destino = destinosConclusao[id];
      const saiNaConclusao = destino ? destino.danificada + destino.retornada : 0;
      if (nova === atual && saiNaConclusao === 0) continue;
      try {
        if (nova > atual) {
          // O servidor decide de onde sai: na produção, primeiro da reserva do orçamento e depois do estoque
          await apiCall(`MateriaPrima/${id}/aumentar`, 'POST', { quantidade: nova - atual });
          alterou = true;
        } else if (nova < atual) {
          const c = reducaoConfirmadaDe(mp);
          if (c) { // sem confirmação não grava (o validar bloqueia antes)
            await processarReducao(mp, nova, c);
            alterou = true;
          }
        }
        // Conclusão da tarefa: o que se perdeu vai para a matéria-prima desperdiçada, o que sobrou volta ao estoque;
        // na tarefa fica só o que foi usado
        if (saiNaConclusao > 0) {
          await apiCall(`MateriaPrima/${id}/reduzir`, 'POST', {
            novaQuantidade: nova - saiNaConclusao,
            quantidadeDesperdicada: destino.danificada,
            quantidadeRetornadaEstoque: destino.retornada,
            motivo: 'Tarefa concluída'
          });
          alterou = true;
        }
      } catch (error) {
        let mensagem = error.message || String(error);
        try { mensagem = JSON.parse(mensagem).mensagem || mensagem; } catch { /* texto simples */ }
        erros.push(`${mp.descricao || 'Matéria-prima'}: ${mensagem}`);
      }
    }
    // Já gravado: não grava de novo. Com erro, o pop-up é perguntado outra vez no próximo Salvar.
    conclusaoRef.current = erros.length > 0 ? null : (conclusaoRef.current && {});
    if (alterou || erros.length > 0) {
      setQuantidadeEditada({});
      setReducoesConfirmadas({});
      await carregarMateriaPrimasDaTarefa();
    }
    if (erros.length > 0) throw new Error('Erro ao alterar matérias-primas: ' + erros.join('; '));
  };

  // Conclusão: o mesmo material do estoque lançado várias vezes na tarefa (ex.: 500 de madeira e depois mais 200)
  // vira um item só, com a quantidade somada (700). Sem material de estoque, cada lançamento fica separado.
  // (vale para a conclusão e para o cancelamento da tarefa)
  const agruparPorMaterial = (quantidadeDe) => {
    const grupos = [];
    const porChave = {};
    materiasPrimas.filter(mp => quantidadeDe(mp) > 0).forEach(mp => {
      const estoqueId = estoqueIdDe(mp);
      const chave = estoqueId ? `e${estoqueId}` : `mp${idMateriaPrima(mp)}`;
      if (!porChave[chave]) {
        porChave[chave] = {
          chave,
          nome: mp.estoqueMaterial?.titulo ?? mp.EstoqueMaterial?.Titulo ?? mp.descricao ?? mp.Descricao ?? 'Matéria-prima',
          temEstoque: !!estoqueId,
          itens: [],
          alocada: 0
        };
        grupos.push(porChave[chave]);
      }
      porChave[chave].itens.push(mp);
      porChave[chave].alocada += quantidadeDe(mp);
    });
    return grupos;
  };
  const gruposConclusao = () => agruparPorMaterial(novaDigitada);
  const gruposCancelamento = () => agruparPorMaterial(quantidadeAtualMP);

  // Reparte a resposta de um material entre os lançamentos dele, tirando primeiro dos mais recentes:
  // { [idMateriaPrima]: { danificada, retornada } }
  const repartirEntreLancamentos = (grupo, perdida, sobra, quantidadeDe) => {
    const destinos = {};
    [...grupo.itens].reverse().forEach(mp => {
      let livre = quantidadeDe(mp);
      const p = Math.min(perdida, livre);
      perdida -= p;
      livre -= p;
      const r = Math.min(sobra, livre);
      sobra -= r;
      if (p + r > 0) destinos[idMateriaPrima(mp)] = { danificada: p, retornada: r };
    });
    return destinos;
  };

  const abrirPopupConclusao = () => {
    const inicial = {};
    gruposConclusao().forEach(g => { inicial[g.chave] = { danificada: '', retornada: '' }; }); // campos vazios
    setConclusaoDistribuicao(inicial);
    setConclusaoModalOpen(true);
  };

  const confirmarConclusao = () => {
    const grupos = gruposConclusao();
    for (const g of grupos) {
      const d = conclusaoDistribuicao[g.chave] || {};
      if (qtdCampo(d.danificada) + qtdCampo(d.retornada) > g.alocada) {
        alert(`${g.nome}: perdida + sobrou não pode passar de ${g.alocada} (o total desse material na tarefa).`);
        return;
      }
      if (!g.temEstoque && qtdCampo(d.retornada) > 0) {
        alert(`${g.nome} não está ligada a um material de estoque: não tem como voltar ao estoque, só pode ser marcada como perdida.`);
        return;
      }
    }
    // Reparte a resposta de cada material entre os lançamentos dele
    const destinos = {};
    grupos.forEach(g => {
      const d = conclusaoDistribuicao[g.chave] || {};
      Object.assign(destinos, repartirEntreLancamentos(g, qtdCampo(d.danificada), qtdCampo(d.retornada), novaDigitada));
    });
    conclusaoRef.current = destinos;
    setConclusaoModalOpen(false);
    // Respondido: o salvar da tarefa continua
    if (onDestinoConfirmado) setTimeout(() => onDestinoConfirmado(), 0);
  };

  // Saiu de "Concluído" antes de salvar: descarta o que tinha sido respondido no pop-up de conclusão
  useEffect(() => {
    if (tarefaConcluindo) return;
    conclusaoRef.current = null;
    setConclusaoModalOpen(false);
  }, [tarefaConcluindo]);

  // Saiu de "Cancelada": descarta o destino que tinha sido informado para o cancelamento
  useEffect(() => {
    if (tarefaCancelada) return;
    cancelamentoRespondidoRef.current = false;
    setCancelModalOpen(false);
    const confirmadas = { ...reducoesConfirmadasRef.current };
    const digitadas = { ...quantidadeEditadaRef.current };
    let mudou = false;
    Object.keys(confirmadas).forEach(id => {
      if (confirmadas[id]?.cancelamento) {
        delete confirmadas[id];
        delete digitadas[id];
        mudou = true;
      }
    });
    if (mudou) {
      setReducoesConfirmadas(confirmadas);
      setQuantidadeEditada(digitadas);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tarefaCancelada]);

  const confirmarCancelamento = () => {
    const grupos = gruposCancelamento();
    for (const g of grupos) {
      const d = cancelDistribuicao[g.chave] || {};
      if (qtdCampo(d.danificada) + qtdCampo(d.retornada) > g.alocada) {
        alert(`${g.nome}: perdida + volta ao estoque não pode passar de ${g.alocada} (o total desse material na tarefa).`);
        return;
      }
      if (!g.temEstoque && qtdCampo(d.retornada) > 0) {
        alert(`${g.nome} não está ligada a um material de estoque: só pode ser marcada como perdida.`);
        return;
      }
    }
    // Só sai da tarefa o que foi informado; campo vazio = nada (o material fica na tarefa como está)
    const confirmadas = { ...reducoesConfirmadasRef.current };
    const digitadas = { ...quantidadeEditadaRef.current };
    grupos.forEach(g => {
      const d = cancelDistribuicao[g.chave] || {};
      const destinos = repartirEntreLancamentos(g, qtdCampo(d.danificada), qtdCampo(d.retornada), quantidadeAtualMP);
      g.itens.forEach(mp => {
        const id = idMateriaPrima(mp);
        const destino = destinos[id];
        if (!destino) return;
        const nova = quantidadeAtualMP(mp) - destino.danificada - destino.retornada;
        confirmadas[id] = { nova, danificada: destino.danificada, retornada: destino.retornada, cancelamento: true };
        digitadas[id] = nova;
      });
    });
    cancelamentoRespondidoRef.current = true;
    setReducoesConfirmadas(confirmadas);
    setQuantidadeEditada(digitadas);
    setErroPendencias('');
    setCancelModalOpen(false);
    // Destino informado: o salvar da tarefa continua
    if (onDestinoConfirmado) setTimeout(() => onDestinoConfirmado(), 0);
  };

  // Abre o pop-up da primeira pendência (botão ✓ do aviso)
  const confirmarPrimeiraPendencia = () => {
    if (faltaDestinoDoCancelamento()) {
      abrirPopupCancelamento();
      return;
    }
    const mp = materiasPrimas.find(precisaConfirmar);
    if (mp) aplicarQuantidade(mp);
  };

  // Abre modal de deleção (novaQuantidade preenchida = redução pela edição de quantidade)
  const abrirModalDelete = (mp, novaQuantidade = null) => {
    setDeleteTarget(mp);
    setReducaoAlvo(novaQuantidade);
    setQtdDanificada('');
    setQtdRetornada('');
    setDeleteModalOpen(true);
  };

  const fecharModalDelete = () => {
    // Fechou sem confirmar: o campo volta para a quantidade atual (ou para a última diminuição já confirmada)
    if (deleteTarget && reducaoAlvo !== null) {
      const id = idMateriaPrima(deleteTarget);
      const confirmada = reducoesConfirmadas[id];
      if (confirmada) setQuantidadeEditada(prev => ({ ...prev, [id]: confirmada.nova }));
      else limparQuantidadeEditada(id);
    }
    setDeleteModalOpen(false);
    setDeleteTarget(null);
    setReducaoAlvo(null);
    setQtdDanificada('');
    setQtdRetornada('');
  };

  // Confirmar no pop-up só guarda a diminuição; ela é gravada no "Salvar" do formulário
  const executarDelete = () => {
    if (!deleteTarget) return;
    const mp = deleteTarget;
    const id = idMateriaPrima(mp);
    const total = quantidadeAtualMP(mp);
    const nova = reducaoAlvo ?? 0;
    const danificada = parseFloat(qtdDanificada) || 0;
    const retornada = parseFloat(qtdRetornada) || 0;
    const retiradas = total - nova;

    if (danificada + retornada !== retiradas) {
      alert(`Informe como estão as ${retiradas} unidade(s) retiradas: perdidas + retornadas deve ser ${retiradas}.`);
      return;
    }
    if (!estoqueIdDe(mp) && retornada > 0) {
      alert('Esta matéria-prima não está vinculada a um material de estoque. Não é possível retornar ao estoque.');
      return;
    }

    setReducoesConfirmadas(prev => ({ ...prev, [id]: { nova, danificada, retornada } }));
    setQuantidadeEditada(prev => ({ ...prev, [id]: nova }));
    setErroPendencias('');
    setDeleteModalOpen(false);
    setDeleteTarget(null);
    setReducaoAlvo(null);
    setQtdDanificada('');
    setQtdRetornada('');
  };

  return (
    <div ref={containerRef} style={{ background: '#fff', borderRadius: 8, border: erroPendencias ? '2px solid #d32f2f' : '1px solid #ddd', padding: 16 }}>
      {!ocultarAdicao && (<>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h4 style={{ margin: 0, color: 'rgb(235, 133, 0)', fontSize: 14, fontWeight: 600 }}>
          ➕ Alocar Materiais para {tipoId === 'orcamento' ? 'Orçamento' : tipoId === 'montagem' ? 'Montagem' : tipoId === 'garantia' ? 'Garantia' : tipoId === 'manutencao' ? 'Manutenção' : 'Tarefa'}
        </h4>
        {onClose && (
          <button 
            type="button"
            onClick={onClose}
            aria-label="Fechar"
            style={{
              backgroundColor: 'rgb(235, 133, 0)',
              color: '#fff',
              border: 'none',
              borderRadius: '50%',
              width: 32,
              height: 32,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 18,
              cursor: 'pointer',
              fontWeight: 700
            }}
          >
            ×
          </button>
        )}
      </div>

      {/* Info Tarefa — oculto para orçamento (envia automático) */}
      {tipoId !== 'orcamento' && (
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <label style={{ fontSize: 12, marginBottom: 4, fontWeight: 600, color: '#555' }}>
            ID {tipoId === 'orcamento' ? 'do Orçamento' : tipoId === 'montagem' ? 'da Montagem' : tipoId === 'garantia' ? 'da Garantia' : tipoId === 'manutencao' ? 'da Manutenção' : 'da Tarefa'}
          </label>
          <input
            type="number"
            readOnly
            value={tarefaId || ''}
            style={{ padding: 8, borderRadius: 6, border: '1px solid #ddd', backgroundColor: '#f9f9f9', fontSize: 12 }}
          />
        </div>

        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <label style={{ fontSize: 12, marginBottom: 4, fontWeight: 600, color: '#555' }}>Data Execução</label>
          <input
            type="date"
            value={novoMaterial.dataExecucao}
            onChange={(e) => setNovoMaterial({ ...novoMaterial, dataExecucao: e.target.value })}
            style={{ padding: 8, borderRadius: 6, border: '1px solid #ddd', fontSize: 12 }}
          />
        </div>
      </div>
      )}

      {/* Valor Total */}
      <div style={{ display: 'flex', flexDirection: 'column', marginBottom: 16 }}>
        <label style={{ fontSize: 12, marginBottom: 4, fontWeight: 600, color: '#555' }}>Valor Total (R$)</label>
        <input
          type="text"
          readOnly
          value={formatBRL(valorTotal)}
          style={{ padding: 8, borderRadius: 6, border: '1px solid #ddd', backgroundColor: '#f9f9f9', fontSize: 12, fontWeight: 600, color: 'rgb(235, 133, 0)' }}
        />
      </div>

      {/* Carregamento de Estoque */}
      {loadingEstoque ? (
        <div style={{ textAlign: 'center', padding: 20, color: '#666', fontSize: 12 }}>
          Carregando materiais de estoque...
        </div>
      ) : materiaisEstoque.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 16, color: '#666', background: '#f5f5f5', borderRadius: 6, fontSize: 12 }}>
          Estoque material vazio, não há matéria-prima para alocar.
        </div>
      ) : (
        <form>
          {/* Lista de Itens para Alocar */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontWeight: 600, margin: '8px 0 12px', color: '#444', fontSize: 12 }}>
              Itens para Alocar
            </div>

            {linhas.map((linha, index) => {
              const material = materiaisEstoque.find(m => (m.id || m.Id) === parseInt(linha.estoqueMaterialId));
              const disponivel = material?.quantidade ?? material?.Quantidade ?? 0;
              const titulo = material?.titulo || material?.descricao || material?.Titulo || material?.Descricao || `Material ${(material?.id || material?.Id) ?? ''}`;
              const unidade = material?.tipoUnidade || material?.TipoUnidade || '';

              return (
                <div key={index} style={{ marginBottom: 12 }}>
                  {/* Select Material + Quantidade (sem Quantidade Prevista - automático para todos) */}
                  <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr auto', gap: 8, alignItems: 'center', marginBottom: 4 }}>
                    <select
                      value={linha.estoqueMaterialId}
                      onChange={(e) => {
                        const novas = [...linhas];
                        novas[index].estoqueMaterialId = e.target.value;
                        setLinhas(novas);
                      }}
                      style={{
                        padding: 8,
                        borderRadius: 6,
                        border: '1px solid #ddd',
                        fontSize: 12,
                        backgroundColor: '#fff'
                      }}
                    >
                      <option value="">Selecione um material</option>
                      {materiaisEstoque.map(m => {
                        const id = m.id || m.Id;
                        const name = m.titulo || m.descricao || m.Titulo || m.Descricao || `Material ${id}`;
                        const disp = m.quantidade ?? m.Quantidade ?? 0;
                        const un = m.tipoUnidade || m.TipoUnidade || '';
                        return (
                          <option key={id} value={id}>
                            {name} {un ? `(${un})` : ''} • Disp: {disp}
                          </option>
                        );
                      })}
                    </select>

                    <input
                      type="number"
                      min="0"
                      step="1"
                      placeholder="Quantidade"
                      value={linha.quantidade}
                      onChange={(e) => {
                        const novas = [...linhas];
                        novas[index].quantidade = Math.floor(Number(e.target.value)) || '';
                        setLinhas(novas);
                      }}
                      style={{
                        padding: 8,
                        borderRadius: 6,
                        border: '1px solid #ddd',
                        fontSize: 12
                      }}
                    />

                    {/* Input Quantidade Prevista removido - agora é automático para todos os módulos */}

                    <div style={{ display: 'flex', gap: 4 }}>
                      <button
                        type="button"
                        onClick={() => setLinhas([...linhas, { estoqueMaterialId: '', quantidade: '', quantidadePrevista: '' }])}
                        style={{
                          padding: '8px 12px',
                          backgroundColor: 'rgb(235, 133, 0)',
                          color: '#fff',
                          border: 'none',
                          borderRadius: 6,
                          cursor: 'pointer',
                          fontSize: 16,
                          fontWeight: 700,
                          minWidth: 40,
                          height: 40,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                        title="Adicionar outro material"
                      >
                        +
                      </button>
                      {index > 0 && (
                        <button
                          type="button"
                          onClick={() => setLinhas(linhas.filter((_, i) => i !== index))}
                          style={{
                            padding: '8px 12px',
                            backgroundColor: '#f44336',
                            color: '#fff',
                            border: 'none',
                            borderRadius: 6,
                            cursor: 'pointer',
                            fontSize: 16,
                            fontWeight: 700,
                            minWidth: 40,
                            height: 40,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                          title="Remover este material"
                        >
                          ×
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Info do Material */}
                  {linha.estoqueMaterialId && (
                    <div style={{ fontSize: 11, color: '#666', marginLeft: 4 }}>
                      {titulo} • Disponível: {disponivel} {unidade || ''}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Obs: Botão Confirmar removido - POST é feito pelo pai (FormularioTarefasDoProjeto) */}
        </form>
      )}
      </>)}

      {/* Lista de Matérias-Primas Alocadas */}
      {tarefaId && materiasPrimas.length > 0 && (
        <div style={{ marginTop: 24, paddingTop: 16, borderTop: '1px solid #eee' }}>
          <h5 style={{ margin: '0 0 12px 0', color: '#666', fontSize: 12, fontWeight: 600 }}>
            Matérias-Primas Alocadas ({materiasPrimas.length})
          </h5>

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
              <thead>
                <tr style={{ backgroundColor: '#f5f5f5', borderBottom: '1px solid #ddd' }}>
                  <th style={{ padding: 8, textAlign: 'left', fontWeight: 600 }}>Descrição</th>
                  <th style={{ padding: 8, textAlign: 'center', fontWeight: 600 }}>Quantidade</th>
                  <th style={{ padding: 8, textAlign: 'center', fontWeight: 600 }}>Valor</th>
                  <th style={{ padding: 8, textAlign: 'center', fontWeight: 600 }}>Ação</th>
                </tr>
              </thead>
              <tbody>
                {materiasPrimas.map((mp) => {
                  const id = idMateriaPrima(mp);
                  const atual = quantidadeAtualMP(mp);
                  const pendente = precisaConfirmar(mp);
                  const confirmada = reducaoConfirmadaDe(mp);
                  const aumento = novaDigitada(mp) > atual ? novaDigitada(mp) - atual : 0;
                  return (
                  <tr key={id} style={{ borderBottom: '1px solid #eee', backgroundColor: '#fafafa' }}>
                    <td style={{ padding: 8 }}>{mp.descricao || '-'}</td>
                    <td style={{ padding: 8, textAlign: 'center' }}>
                      {/* Quantidade editável: aumentar retira do estoque; diminuir abre o pop-up de remoção */}
                      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        <input
                          type="number"
                          min="0"
                          step="any"
                          value={quantidadeEditada[id] ?? atual}
                          onChange={(e) => alterarQuantidadeDigitada(mp, e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') { e.preventDefault(); aplicarQuantidade(mp); }
                          }}
                          disabled={somenteLeitura || ajustandoId === id}
                          title="Aumentar: grava ao salvar o formulário. Diminuir: confirme no ✓ quanto foi perdido e quanto volta ao estoque."
                          style={{ width: 70, padding: '4px 6px', borderRadius: 4, border: pendente ? '2px solid #d32f2f' : '1px solid #ccc', fontSize: 11, textAlign: 'center' }}
                        />
                        <span>{mp.unidadeMedida?.sigla || mp.unidadeSigla || ''}</span>
                        {pendente && (
                          <button
                            type="button"
                            onClick={() => aplicarQuantidade(mp)}
                            title="Confirmar a diminuição: quanto foi perdido e quanto volta ao estoque"
                            className="mp-botao-confirmar"
                          >
                            ✓
                          </button>
                        )}
                      </div>
                      {aumento > 0 && (
                        <div style={{ fontSize: 10, color: '#1565c0', marginTop: 2 }}>+{aumento} · grava ao salvar</div>
                      )}
                      {confirmada && (
                        <div style={{ fontSize: 10, color: '#2e7d32', marginTop: 2 }}>
                          −{atual - confirmada.nova}: {confirmada.danificada} perdida(s), {confirmada.retornada} ao estoque · grava ao salvar
                        </div>
                      )}
                    </td>
                    <td style={{ padding: 8, textAlign: 'center' }}>
                      {(() => {
                        const antes = valorAtualMP(mp);
                        const agora = valorExibidoMP(mp);
                        const diferenca = arredondarValor(agora - antes);
                        if (!agora && !antes) return '-';
                        return (
                          <>
                            <div style={{ fontWeight: diferenca ? 700 : 400 }}>{formatBRL(agora)}</div>
                            {diferenca !== 0 && (
                              <div style={{ fontSize: 10, fontWeight: 700, color: diferenca > 0 ? '#c62828' : '#2e7d32' }}>
                                {diferenca > 0 ? '+' : '−'}{formatBRL(Math.abs(diferenca))}
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </td>
                    <td style={{ padding: 8, textAlign: 'center' }}>
                      <button
                        type="button"
                        disabled={somenteLeitura}
                        onClick={() => abrirModalDelete(mp, 0)}
                        style={{
                          padding: '4px 8px',
                          backgroundColor: '#f44336',
                          color: '#fff',
                          border: 'none',
                          borderRadius: 4,
                          cursor: 'pointer',
                          fontSize: 10,
                          fontWeight: 600
                        }}
                      >
                        ×
                      </button>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {erroPendencias && (
        <div className="mp-aviso-pendencia">
          <span>{erroPendencias}</span>
          <button type="button" className="mp-botao-confirmar" onClick={confirmarPrimeiraPendencia} title="Confirmar agora">✓</button>
        </div>
      )}

      {cancelModalOpen && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 95070, padding: 16 }}>
          <div style={{ width: '560px', maxWidth: '100%', maxHeight: '88vh', overflowY: 'auto', background: '#fff', borderRadius: 10, padding: 20, boxShadow: '0 8px 24px rgba(0,0,0,0.18)' }}>
            <h4 style={{ margin: '0 0 8px', color: 'rgb(235, 133, 0)', fontSize: 16, fontWeight: 700 }}>Tarefa cancelada: e o material alocado?</h4>
            <p style={{ margin: '0 0 14px', color: '#444', fontSize: 13 }}>
              Diga <strong>para cada material</strong> da lista abaixo quanto foi <strong style={{ color: '#1565c0' }}>perdido/danificado</strong> e quanto <strong style={{ color: 'rgb(235, 133, 0)' }}>volta ao estoque</strong>. Se o mesmo material foi lançado mais de uma vez nesta tarefa, as quantidades já aparecem somadas num item só.
              <br />O que você não informar continua na tarefa como está. Deixando tudo vazio, só o status muda para Cancelado.
            </p>
            {gruposCancelamento().map(g => {
              const d = cancelDistribuicao[g.chave] || { danificada: '', retornada: '' };
              const total = g.alocada;
              const fica = total - qtdCampo(d.danificada) - qtdCampo(d.retornada);
              const alterar = (campo, valor) => {
                setCancelDistribuicao(prev => ({ ...prev, [g.chave]: { ...d, [campo]: valorCampo(valor) } }));
              };
              return (
                <div key={g.chave} style={{ padding: 10, border: '1px solid #eee', borderRadius: 8, marginBottom: 10 }}>
                  <div style={{ fontWeight: 600, fontSize: 13, marginBottom: g.itens.length > 1 ? 2 : 6 }}>{g.nome} — {total} alocada(s)</div>
                  {g.itens.length > 1 && (
                    <div style={{ fontSize: 11, color: '#777', marginBottom: 6 }}>
                      Lançado {g.itens.length} vezes nesta tarefa ({g.itens.map(mp => quantidadeAtualMP(mp)).join(' + ')}), somado aqui
                    </div>
                  )}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                    <label style={{ fontSize: 12, color: '#1565c0', fontWeight: 600 }}>
                      Perdida / Danificada
                      <input type="number" min="0" max={total} step="1" value={d.danificada} onChange={(e) => alterar('danificada', e.target.value)} style={{ width: '100%', padding: 8, borderRadius: 6, border: '1px solid #ddd', marginTop: 4 }} />
                    </label>
                    <label style={{ fontSize: 12, color: 'rgb(235, 133, 0)', fontWeight: 600 }}>
                      Volta ao estoque
                      <input type="number" min="0" max={total} step="1" value={d.retornada} onChange={(e) => alterar('retornada', e.target.value)} disabled={!g.temEstoque} style={{ width: '100%', padding: 8, borderRadius: 6, border: '1px solid #ddd', marginTop: 4 }} />
                    </label>
                  </div>
                  <div style={{ marginTop: 6, fontSize: 12, fontWeight: 600, color: fica < 0 ? '#d32f2f' : '#555' }}>
                    {fica < 0 ? `Passou do alocado: perdida + volta ao estoque não pode ser maior que ${total}.` : `Continua na tarefa: ${fica}`}
                  </div>
                </div>
              );
            })}
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 6 }}>
              <button type="button" onClick={() => setCancelModalOpen(false)} style={{ padding: '8px 14px', background: '#9e9e9e', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: 12 }}>Voltar</button>
              <button type="button" onClick={confirmarCancelamento} style={{ padding: '8px 14px', background: 'rgb(235, 133, 0)', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 700, fontSize: 12 }}>Confirmar e salvar</button>
            </div>
          </div>
        </div>
      )}

      {conclusaoModalOpen && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 95070, padding: 16 }}>
          <div style={{ width: '560px', maxWidth: '100%', maxHeight: '88vh', overflowY: 'auto', background: '#fff', borderRadius: 10, padding: 20, boxShadow: '0 8px 24px rgba(0,0,0,0.18)' }}>
            <h4 style={{ margin: '0 0 8px', color: 'rgb(235, 133, 0)', fontSize: 16, fontWeight: 700 }}>Sobrou alguma matéria-prima?</h4>
            <p style={{ margin: '0 0 14px', color: '#444', fontSize: 13, lineHeight: 1.5 }}>
              Aqui você informa quanto foi <strong style={{ color: '#1565c0' }}>perdido/danificado</strong> durante a tarefa e quanto <strong style={{ color: 'rgb(235, 133, 0)' }}>sobrou para voltar ao estoque</strong>. Na tarefa fica só o que foi realmente usado.
              <br />Responda <strong>para cada material</strong> da lista abaixo (ex.: madeira, depois ferro...). Se o mesmo material foi lançado mais de uma vez nesta tarefa, as quantidades já aparecem somadas num item só.
              <br />Se toda a matéria-prima foi usada, sem perda e sem sobra, deixe os campos vazios e clique em <strong>Concluir</strong>.
            </p>
            {gruposConclusao().map(g => {
              const d = conclusaoDistribuicao[g.chave] || { danificada: '', retornada: '' };
              const alocada = g.alocada;
              const usada = alocada - qtdCampo(d.danificada) - qtdCampo(d.retornada);
              const alterar = (campo, valor) => {
                setConclusaoDistribuicao(prev => ({ ...prev, [g.chave]: { ...d, [campo]: valorCampo(valor) } }));
              };
              return (
                <div key={g.chave} style={{ padding: 10, border: '1px solid #eee', borderRadius: 8, marginBottom: 10 }}>
                  <div style={{ fontWeight: 600, fontSize: 13, marginBottom: g.itens.length > 1 ? 2 : 6 }}>{g.nome} — {alocada} alocada(s)</div>
                  {g.itens.length > 1 && (
                    <div style={{ fontSize: 11, color: '#777', marginBottom: 6 }}>
                      Lançado {g.itens.length} vezes nesta tarefa ({g.itens.map(mp => novaDigitada(mp)).join(' + ')}), somado aqui
                    </div>
                  )}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                    <label style={{ fontSize: 12, color: '#1565c0', fontWeight: 600 }}>
                      Perdida / Danificada
                      <input type="number" min="0" max={alocada} step="1" value={d.danificada} onChange={(e) => alterar('danificada', e.target.value)} style={{ width: '100%', padding: 8, borderRadius: 6, border: '1px solid #ddd', marginTop: 4 }} />
                    </label>
                    <label style={{ fontSize: 12, color: 'rgb(235, 133, 0)', fontWeight: 600 }}>
                      Sobrou (volta ao estoque)
                      <input type="number" min="0" max={alocada} step="1" value={d.retornada} onChange={(e) => alterar('retornada', e.target.value)} disabled={!g.temEstoque} style={{ width: '100%', padding: 8, borderRadius: 6, border: '1px solid #ddd', marginTop: 4 }} />
                    </label>
                  </div>
                  <div style={{ marginTop: 6, fontSize: 12, fontWeight: 600, color: usada < 0 ? '#d32f2f' : '#2e7d32' }}>
                    {usada < 0 ? `Passou do alocado: perdida + sobrou não pode ser maior que ${alocada}.` : `Usada na tarefa: ${usada}`}
                  </div>
                </div>
              );
            })}
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 6 }}>
              <button type="button" className="btn-cancelar-forte" onClick={() => setConclusaoModalOpen(false)} style={{ padding: '8px 14px', borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: 12 }}>Cancelar</button>
              <button type="button" onClick={confirmarConclusao} style={{ padding: '8px 14px', background: 'rgb(235, 133, 0)', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 700, fontSize: 12 }}>Concluir</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de deleção custom */}
      {deleteModalOpen && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 95070
        }}>
          <div style={{ width: '420px', maxWidth: '90vw', background: '#fff', borderRadius: 10, padding: 20, boxShadow: '0 8px 24px rgba(0,0,0,0.18)' }}>
            <>
              <h4 style={{ margin: '0 0 12px', color: 'rgb(235, 133, 0)', fontSize: 16, fontWeight: 700 }}>
                {reducaoAlvo ? 'Diminuir Quantidade' : 'Remover Matéria-Prima'}
              </h4>
              <p style={{ margin: '0 0 16px', color: '#444', fontSize: 14 }}>
                {reducaoAlvo
                  ? `De ${quantidadeAtualMP(deleteTarget)} para ${reducaoAlvo}: como estão as ${quantidadeAtualMP(deleteTarget) - reducaoAlvo} unidade(s) retiradas?`
                  : `Como estão as ${quantidadeAtualMP(deleteTarget)} unidade(s)?`} A alteração é gravada ao salvar o formulário.
              </p>
              <div style={{ marginBottom: 12, color: '#555', fontSize: 12, lineHeight: 1.4 }}>
                <div style={{ fontWeight: 600, color: '#1565c0' }}>
                  Danificada / Perdida <span style={{ color: '#666', fontWeight: 400 }}>(Matéria-prima foi danificada ou perdida durante o processo.)</span>
                </div>
                <div style={{ fontWeight: 600, color: 'rgb(235, 133, 0)' }}>
                  Disponível para estoque <span style={{ color: '#666', fontWeight: 400 }}>(Retornar matéria-prima ao estoque. Pronta para reutilizar.)</span>
                </div>
              </div>
              <div style={{ marginBottom: 12 }}>
                <label style={{ display: 'block', marginBottom: 4, color: '#1565c0', fontWeight: 600, fontSize: 12 }}>Danificada / Perdida</label>
                <input
                  type="number"
                  min="0"
                  step="1"
                  placeholder="0"
                  value={qtdDanificada}
                  onChange={(e) => setQtdDanificada(Math.floor(Number(e.target.value)) || '')}
                  style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #ddd' }}
                />
              </div>
              <div style={{ marginBottom: 12 }}>
                <label style={{ display: 'block', marginBottom: 4, color: 'rgb(235, 133, 0)', fontWeight: 600, fontSize: 12 }}>Retornar para Estoque</label>
                <input
                  type="number"
                  min="0"
                  step="1"
                  placeholder="0"
                  value={qtdRetornada}
                  onChange={(e) => setQtdRetornada(Math.floor(Number(e.target.value)) || '')}
                  style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #ddd' }}
                />
              </div>
              <p style={{ margin: '0 0 12px', color: '#666', fontSize: 11 }}>
                Total alocado: {parseFloat(deleteTarget?.quantidade ?? deleteTarget?.Quantidade ?? 0) || 0}
              </p>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={fecharModalDelete}
                  style={{ padding: '8px 14px', background: '#9e9e9e', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: 12 }}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={executarDelete}
                  disabled={deleting}
                  style={{ padding: '8px 14px', background: deleting ? '#ccc' : 'rgb(235, 133, 0)', color: '#fff', border: 'none', borderRadius: 6, cursor: deleting ? 'not-allowed' : 'pointer', fontWeight: 700, fontSize: 12 }}
                >
                  Confirmar
                </button>
              </div>
            </>
          </div>
        </div>
      )}
    </div>
  );
}

export default forwardRef(FormularioMateriaPrima);
